import { Button, Statistic, Typography } from 'antd'
import React from 'react'
import { useNavigate } from 'react-router-dom';
import CountUp from 'react-countup';
import {
    CheckCircleOutlined,
    DatabaseOutlined,
    ExperimentOutlined,
    GlobalOutlined,
    MedicineBoxOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import BarChart from './BarChart';
import './index.css';
import GsrdWch from '../../components/GsrdWch';
import RgnetIntro from './RgnetIntro';

const { Title, Text } = Typography;

const metricCards = [
    {
        imageUrl: 'https://gsrd-server.tos-cn-beijing.volces.com/yangben.png',
        value: 26317,
        title: 'Probands',
        size: '45px',
        tone: 'teal'
    },
    {
        imageUrl: 'https://gsrd-server.tos-cn-beijing.volces.com/Participants.png',
        value: 9965,
        title: 'Relatives',
        size: '45px',
        tone: 'blue'
    },
    {
        imageUrl: 'https://gsrd-server.tos-cn-beijing.volces.com/home03.png',
        value: 15857,
        title: 'Controls',
        size: '42px',
        tone: 'green'
    },
    {
        imageUrl: 'https://gsrd-server.tos-cn-beijing.volces.com/wgs.png',
        value: 42.2,
        title: 'Mean coverage',
        size: '54px',
        suffix: 'X',
        tone: 'gold'
    },
    {
        imageUrl: 'https://gsrd-server.tos-cn-beijing.volces.com/home01.png',
        value: 531,
        title: 'SNVs &amp; InDels',
        size: '38px',
        suffix: 'million',
        tone: 'cyan'
    },
    {
        imageUrl: 'https://gsrd-server.tos-cn-beijing.volces.com/home03.png',
        value: 7400,
        title: 'SVs per genome',
        size: '42px',
        tone: 'slate'
    }
];

const aims = [
    {
        icon: <ExperimentOutlined />,
        text: <>High-depth genome sequencing <strong>100,000 patients</strong> and <strong>20,000 matched controls</strong></>
    },
    {
        icon: <GlobalOutlined />,
        text: <><strong>Nationwide coverage</strong> across all provinces and <strong>diverse ethnic groups</strong></>
    },
    {
        icon: <MedicineBoxOutlined />,
        text: <>Advance <strong>genetic diagnosis, discovery, and precision medicine</strong></>
    }
];

const criteria = [
    {
        icon: <CheckCircleOutlined />,
        text: 'Clinically diagnosed or suspected rare diseases'
    },
    {
        icon: <CheckCircleOutlined />,
        text: 'Early-onset and multisystem complex disorders'
    },
    {
        icon: <CheckCircleOutlined />,
        text: 'Familial and hereditary systemic conditions'
    }
];

function HeroSection({ onSearch }) {
    return (
        <section className="display-hero-section">
            <div className="display-hero-overlay" />
            <div className="display-hero-inner">
                <img
                    className="display-hero-logo"
                    src="https://gsrd-server.tos-cn-beijing.volces.com/Organizations/thanks/gsrd-logo-new2.png"
                    alt="GSRD-100K WCH"
                />
                <Title className="display-hero-title">
                    Genome Sequencing of Rare Disease - 100K<sup className="gsrd-wch-mark">WCH</sup>
                </Title>
                <Button
                    type="primary"
                    className="display-action-button"
                    onClick={onSearch}
                >
                    <SearchOutlined /> Variant Search
                </Button>
            </div>
        </section>
    );
}

function InfoCards() {
    const renderList = (items) => items.map((item, index) => (
        <li key={index}>
            <span className="display-info-icon">{item.icon}</span>
            <span>{item.text}</span>
        </li>
    ));

    return (
        <section className="display-info-section">
            <div className="display-section-shell">
                <div className="display-info-panel">
                    {/* 首页项目目标和入组标准外层统一成整体卡片，内部两栏保持信息类型区分 */}
                    <div className="display-info-grid">
                        <article className="display-info-card is-aims">
                            <h2>Aims of <GsrdWch /></h2>
                            <ul>{renderList(aims)}</ul>
                        </article>
                        <article className="display-info-card is-criteria">
                            <h2>Inclusion criteria</h2>
                            <ul>{renderList(criteria)}</ul>
                        </article>
                    </div>
                </div>
            </div>
        </section>
    );
}

function MetricCards() {
    return (
        <section className="display-metric-section">
            <div className="display-section-shell">
                <div className="display-metrics-panel">
                    <div className="display-metrics-title">
                        <span className="display-metrics-title-icon">
                            <DatabaseOutlined />
                        </span>
                        <h2>Current Data Release</h2>
                    </div>
                    {/* 六个核心指标保留原始数据，仅调整为居中的科研数据卡片阵列 */}
                    <div className="display-stat-grid">
                        {metricCards.map((card) => (
                            <MetricCard key={card.title} {...card} />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

function MetricCard({ imageUrl, value, title, size, suffix, tone }) {
    return (
        <article className={`display-stat-card is-${tone}`}>
            <div className="display-stat-icon">
                {/* 首页指标图标跟随卡片主题色，保持数据卡片可识别 */}
                <img src={imageUrl} style={{ width: size ?? '50px' }} alt="" />
            </div>
            <Statistic
                value={value}
                formatter={(statValue) => (
                    <div className="display-stat-value">
                        <CountUp
                            end={statValue}
                            separator=","
                            decimals={Number.isInteger(Number(statValue)) ? 0 : 1}
                        />
                        {suffix && <span>{suffix}</span>}
                    </div>
                )}
            />
            <Text className="display-stat-label">
                <span dangerouslySetInnerHTML={{ __html: title }} />
            </Text>
        </article>
    );
}

function ProbandsPanel() {
    return (
        <section className="display-probands-section">
            <div className="display-section-shell">
                <BarChart />
            </div>
        </section>
    );
}

export default function Display({ setKey }) {
    const navigate = useNavigate()

    const handleSearch = () => {
        setKey('searchInput');
        navigate('/searchInput', { state: { pages: "upload" } })
    };

    return (
        <div className="display-page">
            <img
                src="https://gsrd-server.tos-cn-beijing.volces.com/righttop.png"
                className="display-corner-img display-corner-left"
                alt=""
            />
            <img
                src="https://gsrd-server.tos-cn-beijing.volces.com/lefttop.png"
                className="display-corner-img display-corner-right"
                alt=""
            />
            <HeroSection onSearch={handleSearch} />
            <InfoCards />
            <MetricCards />
            <RgnetIntro onOpen={() => navigate('/rgnet')} />
            <ProbandsPanel />
            <div className="display-bottom-spacer" />
        </div>
    )
}
